/**
 * Stock allocator - the multi-row editing UI for assigning stock rows
 * to a dispensed quantity.
 *
 * Renders stock row select dropdowns, quantity inputs, add/remove row
 * controls, contextual warnings (no stock, expired stock), and an
 * allocation progress badge. Purely controlled: receives allocations +
 * onChange callback. Does not fetch data - available stock rows and
 * metadata are passed in as props.
 */
import { parseISO } from 'date-fns'
import { AlertTriangle, CircleMinus, CirclePlus, Clock, Package } from 'lucide-react'

import { type BatchAllocationItem, roundQuantity } from '@snootsvet/shared'

import { NumericInput } from '@/frontend/components/numeric-input'
import { Button } from '@/frontend/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import { StockWarningInClinic } from '@/frontend/features/orders/stock-warning'
import {
  type StockAllocationBatch,
  type StockAllocationStatus,
  stockRowTextClass,
} from '@/frontend/lib/stock'
import { cn } from '@/frontend/lib/utils'

/** Formats expiry date for display in the batch select dropdown. */
function formatExpiry(expiryDate: string | null): string | null {
  if (!expiryDate) return null
  return parseISO(expiryDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

/**
 * Sentinel value representing unbatched stock (a real stock row where the
 * batch number is null). Base UI Select treats null/undefined as "no
 * selection" and shows the placeholder, so we need a non-null stand-in.
 * The null byte prefix prevents collisions with real batch numbers.
 * Only used inside BatchSelect - callers work with plain nulls.
 */
const UNBATCHED_SENTINEL = '\0__unbatched__'

/**
 * Batch select dropdown that handles the null-batch-number problem internally.
 * Accepts and emits `string | null` - the sentinel conversion is invisible
 * to the rest of the editor.
 */
function BatchSelect({
  batches,
  value,
  onChange,
  className,
}: {
  batches: StockAllocationBatch[]
  value: string | null
  onChange: (batchNumber: string | null) => void
  className?: string
}) {
  const toSelect = (bn: string | null) => bn ?? UNBATCHED_SENTINEL
  const fromSelect = (bn: string) => (bn === UNBATCHED_SENTINEL ? null : bn)

  const items = batches.map((b) => ({
    label: b.batchNumber ?? 'No batch number',
    value: toSelect(b.batchNumber),
  }))

  return (
    <Select
      value={toSelect(value)}
      onValueChange={(v) => {
        if (v) onChange(fromSelect(v))
      }}
      items={items}
    >
      <SelectTrigger className={cn('h-8 flex-1', className)}>
        <SelectValue placeholder="Select batch…" />
      </SelectTrigger>
      <SelectContent align="start">
        {batches.map((b) => {
          const selectValue = toSelect(b.batchNumber)
          const textClass = stockRowTextClass(b.status)
          const isExpired = b.status === 'expired' || b.status === 'expired_depleted'
          const expiryLabel = formatExpiry(b.expiryDate)
          return (
            <SelectItem
              key={selectValue}
              value={selectValue}
              highlight="none"
              className="py-1.5 focus:bg-muted/50"
            >
              <div className="flex w-full items-center gap-2">
                {isExpired ? (
                  <Clock className={cn('size-3.5 shrink-0', textClass)} />
                ) : (
                  <Package className={cn('size-3.5 shrink-0', textClass)} />
                )}
                <div className="flex flex-1 flex-col gap-0.5">
                  <span className="truncate font-mono text-sm font-medium">
                    {b.batchNumber ?? 'No batch number'}
                  </span>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>
                      First stocked{' '}
                      {new Date(b.createdAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                    {expiryLabel && (
                      <span className={isExpired ? 'font-medium text-destructive-accent' : ''}>
                        {isExpired ? `Expired ${expiryLabel}` : `Expires ${expiryLabel}`}
                      </span>
                    )}
                  </div>
                </div>
                <span className={cn('shrink-0 text-sm font-medium tabular-nums', textClass)}>
                  {b.quantity} in stock
                </span>
              </div>
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

export interface StockAllocationEditorProps {
  /** Current allocation rows. */
  allocations: BatchAllocationItem[]
  /** Called whenever the allocations change (add, remove, edit). */
  onChange: (allocations: BatchAllocationItem[]) => void
  /** Required quantity to fully allocate. */
  quantity: number
  /** Batches available for selection. */
  availableBatches: StockAllocationBatch[]
  /** Server-computed stock status. */
  stockStatus: StockAllocationStatus
  /** Clinic ID for generating inventory links in stock warnings. */
  clinicId?: string
  /** Product name for generating inventory links in stock warnings. */
  productName?: string
}

/**
 * Multi-row batch allocation editor.
 *
 * Fully controlled - works directly with the `allocations` prop, no local
 * state. Clicking "+" auto-selects the next available batch (FEFO order)
 * rather than adding an empty placeholder row, which avoids the ambiguity
 * between "unselected" and "unbatched stock" (both null).
 */
export function StockAllocationEditor({
  allocations,
  onChange,
  quantity,
  availableBatches,
  stockStatus,
  clinicId,
  productName,
}: StockAllocationEditorProps) {
  const noBatches = stockStatus === 'no-stock'

  const allocated = allocations.reduce((sum, a) => sum + a.quantity, 0)

  /** Batch numbers already used in the current allocations. */
  const usedBatchNumbers = new Set(allocations.map((a) => a.batchNumber))

  /** Next available batch not already allocated, in FEFO order. */
  const nextAvailableBatch = availableBatches.find((b) => !usedBatchNumbers.has(b.batchNumber))

  const updateRow = (idx: number, patch: Partial<BatchAllocationItem>) => {
    onChange(allocations.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }

  const removeRow = (idx: number) => {
    onChange(allocations.filter((_, i) => i !== idx))
  }

  /** Adds the next available batch with the remaining quantity. */
  const addRow = () => {
    if (!nextAvailableBatch) return
    // Round the remainder — `allocated` sums grid values but the sum itself
    // drifts (0.07 + 0.07 + 0.07 = 0.21000000000000002), and the API rejects
    // quantities that aren't multiples of 0.01. `nextAvailableBatch.quantity`
    // is already grid-aligned by `computeStockAllocation`.
    const remaining = Math.max(0, roundQuantity(quantity - allocated))
    const rowQty = Math.max(0, Math.min(remaining, nextAvailableBatch.quantity))
    onChange([...allocations, { batchNumber: nextAvailableBatch.batchNumber, quantity: rowQty }])
  }

  return (
    <div className="flex flex-col gap-1">
      <StockWarningInClinic
        stockStatus={stockStatus}
        clinicId={clinicId}
        productName={productName}
        className="mb-1"
      />

      {!noBatches &&
        allocations.map((row, j) => {
          const batch = availableBatches.find((b) => b.batchNumber === row.batchNumber)
          const isExpired = batch?.status === 'expired' || batch?.status === 'expired_depleted'
          const isOverAssigned = batch != null && row.quantity > batch.quantity
          const rowWarning = isExpired
            ? 'Expired batch'
            : isOverAssigned
              ? 'Quantity exceeds available stock'
              : null

          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no stable identity
            <div key={j} className="flex items-center gap-2">
              <BatchSelect
                batches={availableBatches}
                value={row.batchNumber}
                onChange={(bn) => updateRow(j, { batchNumber: bn })}
                className={isExpired ? 'ring-2 ring-warning rounded-md' : undefined}
              />

              <NumericInput
                className={`h-8 w-14 bg-card ${isOverAssigned ? 'ring-2 ring-warning' : ''}`}
                value={row.quantity}
                onChange={(v) => updateRow(j, { quantity: Math.max(0, v ?? 0) })}
              />

              <div className="flex items-center">
                <div className="flex size-6 items-center justify-center">
                  {rowWarning && (
                    <span title={rowWarning}>
                      <AlertTriangle
                        className="size-4 text-warning-accent"
                        aria-label={rowWarning}
                      />
                    </span>
                  )}
                </div>
                <div className="flex size-6 items-center justify-center">
                  {allocations.length > 1 && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-6"
                      onClick={() => removeRow(j)}
                      title="Remove batch"
                      aria-label="Remove batch row"
                    >
                      <CircleMinus className="size-4 text-destructive-accent" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )
        })}

      {!noBatches && nextAvailableBatch && (
        <Button type="button" variant="outline" onClick={addRow}>
          <CirclePlus className="size-4" />
          Add batch
        </Button>
      )}
    </div>
  )
}
